import React, { useEffect, useState, useMemo } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, addMonths, subMonths, isSameMonth, isSameDay, parseISO, parse } from "date-fns";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtTime, todayISO } from "@/lib/tps";
import { StatusBadge } from "@/components/StatusBadge";

export default function CalendarView() {
  const [cursor, setCursor] = useState(new Date());
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const start = format(startOfWeek(startOfMonth(cursor)), "yyyy-MM-dd");
    const end = format(endOfWeek(endOfMonth(cursor)), "yyyy-MM-dd");
    api.get(`/orders?start=${start}&end=${end}`).then((r) => setOrders(r.data));
  }, [cursor]);

  const days = useMemo(() => {
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(cursor)),
      end: endOfWeek(endOfMonth(cursor)),
    });
  }, [cursor]);

  const ordersByDay = useMemo(() => {
    const map = {};
    orders.forEach((o) => {
      const d = o.required_date;
      if (!d) return;
      (map[d] = map[d] || []).push(o);
    });
    return map;
  }, [orders]);

  const today = todayISO();

  return (
    <div className="space-y-3 animate-fade-in" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl md:text-3xl">Calendar</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 border-[#E8E3DF]" onClick={() => setCursor(subMonths(cursor, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-heading text-lg w-44 text-center">{format(cursor, "MMMM yyyy")}</div>
          <Button variant="outline" size="icon" className="h-8 w-8 border-[#E8E3DF]" onClick={() => setCursor(addMonths(cursor, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
        </div>
      </div>

      <Card className="border-[#E8E3DF] overflow-hidden">
        <div className="grid grid-cols-7 bg-[#FCF9F9] border-b border-[#E8E3DF]">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="text-[11px] uppercase tracking-wider text-[#4B5563] py-2 px-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const dayOrders = ordersByDay[key] || [];
            const inMonth = isSameMonth(d, cursor);
            const isToday = key === today;
            return (
              <div key={key} className={`min-h-[88px] md:min-h-[110px] border-b border-r border-[#E8E3DF] p-1.5 ${inMonth ? "bg-white" : "bg-[#FDFBF7]"}`}
                data-testid={`cal-day-${key}`}>
                <div className={`text-xs ${isToday ? "bg-[#B86F77] text-white font-medium rounded-full w-6 h-6 flex items-center justify-center" : inMonth ? "text-[#141E30]" : "text-[#4B5563]/50"}`}>
                  {format(d, "d")}
                </div>
                <div className="mt-1 space-y-0.5">
                  {dayOrders.slice(0, 3).map((o) => (
                    <Link key={o.id} to={`/orders/${o.id}`} className="block text-[10px] truncate bg-[#F3E8E9] text-[#141E30] rounded px-1 py-0.5 hover:bg-[#B86F77] hover:text-white" data-testid={`cal-event-${o.order_id}`}>
                      {fmtTime(o.required_time)} {o.customer_name}
                    </Link>
                  ))}
                  {dayOrders.length > 3 && <div className="text-[10px] text-[#4B5563]">+{dayOrders.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
